import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { usePostHog } from '@posthog/react';
import {
  captureFeedbackSurveyShown,
  sendProductFeedback,
  subscribeOpenFeedbackSurvey,
} from '../../analytics/productFeedback';
import { getOrCreateDockPortal } from './tawkToWidget/api';
import styles from './FeedbackDock.module.css';

const EASE = [0.22, 1, 0.36, 1];
const MAX_LENGTH = 2000;
const DEFAULT_QUESTION = 'How can we make the app better?';
const DEFAULT_PLACEHOLDER = 'A bug, something confusing, or an idea.';

function FeedbackIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <path
        d="M13.6 4.8 19.2 10.4 9.2 20.4 3.8 20.8 4.2 15.4 13.6 4.8Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="m12.1 6.3 5.6 5.6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
      <path
        d="M6 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function promptFor(survey) {
  const question = survey?.questions?.[0];
  const text = typeof question?.question === 'string' ? question.question.trim() : '';
  const fromQuestion = typeof question?.placeholder === 'string' ? question.placeholder.trim() : '';
  const fromAppearance = typeof survey?.appearance?.placeholder === 'string'
    ? survey.appearance.placeholder.trim()
    : '';
  return {
    question: text || DEFAULT_QUESTION,
    placeholder: fromQuestion || fromAppearance || DEFAULT_PLACEHOLDER,
  };
}

export default function FeedbackDock({ source = 'meet_an_expert' } = {}) {
  const posthog = usePostHog();
  const reduceMotion = useReducedMotion();
  const titleId = useId();
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const thanksRef = useRef(null);
  const statusRef = useRef('idle');
  const shownForRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [restoreFocus, setRestoreFocus] = useState(false);
  const [draft, setDraft] = useState('');
  const [status, setStatus] = useState('idle');
  const [survey, setSurvey] = useState(null);
  const prompt = promptFor(survey);
  const canSend = draft.trim().length > 0 && status !== 'sending';
  statusRef.current = status;
  const transition = reduceMotion ? { duration: 0 } : { duration: 0.26, ease: EASE };

  useEffect(() => subscribeOpenFeedbackSurvey(posthog, (next) => {
    setSurvey((current) => (current?.id === next?.id ? current : next));
  }), [posthog]);

  const close = useCallback(() => {
    if (statusRef.current === 'sent') setDraft('');
    setStatus('idle');
    setRestoreFocus(true);
    setOpen(false);
  }, []);

  const openPanel = useCallback(() => {
    setStatus('idle');
    setOpen(true);
  }, []);

  const dockRef = useCallback((node) => {
    if (!node || !restoreFocus) return;
    node.focus();
    setRestoreFocus(false);
  }, [restoreFocus]);

  useEffect(() => {
    if (!open) {
      shownForRef.current = null;
      return undefined;
    }
    if (!survey || shownForRef.current === survey.id) return undefined;
    shownForRef.current = survey.id;
    captureFeedbackSurveyShown(posthog, survey, source);
    return undefined;
  }, [open, posthog, source, survey]);

  useEffect(() => {
    if (!open || status === 'sent') return undefined;
    const id = window.setTimeout(() => inputRef.current?.focus(), reduceMotion ? 0 : 40);
    return () => window.clearTimeout(id);
  }, [open, reduceMotion, status]);

  useEffect(() => {
    if (!open || status !== 'sent') return undefined;
    thanksRef.current?.focus();
    return undefined;
  }, [open, status]);

  useEffect(() => {
    if (!open || status !== 'sent') return undefined;
    const id = window.setTimeout(close, 2600);
    return () => window.clearTimeout(id);
  }, [close, open, status]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const root = panelRef.current;
      if (!root) return;
      const nodes = [...root.querySelectorAll('button:not([disabled]), textarea:not([disabled])')];
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const onPointerDown = (event) => {
      const root = panelRef.current;
      if (!root || root.contains(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      close();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [close, open]);

  const submit = (event) => {
    event.preventDefault();
    if (!canSend) return;
    setStatus('sending');
    const sent = sendProductFeedback(posthog, { message: draft, source, survey });
    setStatus(sent ? 'sent' : 'error');
  };

  return createPortal(
    <div className={styles.root}>
      {open ? (
          <motion.section
            key="panel"
            ref={panelRef}
            className={styles.panel}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            style={{ originX: 1, originY: 1 }}
            initial={false}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={transition}
          >
            <div className={styles.panelHeader}>
              <span className={styles.iconCircle} aria-hidden="true">
                <FeedbackIcon />
              </span>
              <div className={styles.panelHeading}>
                <p className={styles.eyebrow}>Feedback</p>
                <h2 id={titleId} className={styles.panelTitle}>
                  {status === 'sent' ? 'Thanks for the note' : prompt.question}
                </h2>
              </div>
              <button type="button" className={styles.close} onClick={close} aria-label="Close feedback">
                <CloseIcon />
              </button>
            </div>
            {status === 'sent' ? (
              <p ref={thanksRef} className={styles.thanks} role="status" tabIndex={-1}>
                We got it and will use it to improve the app.
              </p>
            ) : (
              <form onSubmit={submit}>
                <textarea
                  ref={inputRef}
                  className={styles.input}
                  name="feedback"
                  aria-labelledby={titleId}
                  maxLength={MAX_LENGTH}
                  placeholder={prompt.placeholder}
                  value={draft}
                  rows={4}
                  onChange={(event) => {
                    setDraft(event.target.value);
                    if (status === 'error') setStatus('idle');
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                      event.preventDefault();
                      event.currentTarget.form?.requestSubmit();
                    }
                  }}
                />
                {status === 'error' && (
                  <p className={styles.error} role="alert">
                    We couldn't send that just now. Please try again.
                  </p>
                )}
                <button type="submit" className={styles.send} disabled={!canSend}>
                  {status === 'sending' ? 'Sending…' : 'Send feedback'}
                </button>
              </form>
            )}
          </motion.section>
        ) : (
          <motion.button
            key="dock"
            type="button"
            ref={dockRef}
            className={styles.dock}
            onClick={openPanel}
            aria-haspopup="dialog"
            aria-label="Share feedback. Help us improve the app."
            style={{ originX: 1, originY: 1 }}
            initial={false}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={transition}
            whileHover={reduceMotion ? undefined : { y: -2 }}
            whileTap={reduceMotion ? undefined : { scale: 0.97 }}
          >
            <span className={styles.iconCircle} aria-hidden="true">
              <FeedbackIcon />
            </span>
            <span className={styles.dockText}>
              <span className={styles.dockTitle}>Share feedback</span>
              <span className={styles.dockSub}>Help us improve the app</span>
            </span>
            <span className={styles.arrowBtn} aria-hidden="true">
              <ChevronRight />
            </span>
          </motion.button>
        )}
    </div>,
    getOrCreateDockPortal()
  );
}
